import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Loader2, ShieldCheck } from "lucide-react";
import { API_BASE_URL } from "../api/config";
import { pageRoutes } from "../utils";

function isAdminRole(role) {
  return ["admin", "cashier", "super_admin"].includes(role);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    if (status === "loading") {
      return;
    }
    setStatus("loading");
    setErrorMessage("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || !payload.ok) {
        throw new Error(
          payload.error || payload.message || "Unable to sign in."
        );
      }

      if (!isAdminRole(payload.user?.role)) {
        throw new Error(
          "You do not have permission to access the admin dashboard."
        );
      }

      navigate(pageRoutes.AdminDashboard, { replace: true });
    } catch (error) {
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "Unable to sign in.");
    }
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3] px-4 py-16">
      <div className="mx-auto max-w-md rounded-2xl border border-[#C9A227]/20 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-[#722F37]" />
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#A88520]">
              Admin
            </p>
            <h1 className="font-serif-elegant text-3xl font-bold text-[#722F37]">
              Sign in
            </h1>
          </div>
        </div>

        {errorMessage && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              autoComplete="email"
              required
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              autoComplete="current-password"
              required
            />
          </label>
          <button
            type="submit"
            disabled={status === "loading"}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C] disabled:opacity-70"
          >
            {status === "loading" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}
            Sign in
          </button>
        </form>

        <div className="mt-5 text-center text-sm">
          <Link className="font-semibold text-[#722F37] underline" to={pageRoutes.ForgotPassword}>
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
}
